<template lang="pug">
    .product-import(v-loading="categories.loading")
        //- (v-if="this.actions.initialize === null")
        .alert.info(v-if="info")
            p {{ info }}
        .alert.danger(v-if="error")
            p {{ error }}
            a(href="#" @click.prevent.stop="initialize") {{ $t('Try again') }}
        .form(v-else)
            .import-info-panel(v-if="currentImport")
                //- When the microservice manages a StatusLog we render the shared StatusLog
                //- component the rest of the admin uses, fed by the status-log.{id} channel.
                //- The per-status blocks below are the legacy fallback, kept because several
                //- microservices manage no status log at all - never both at once.
                .status-logs-container.z-depth-1.live-status(v-if="useLiveStatusLog")
                    StatusLog(:log="statusLog")
                        template(slot="actions")
                            .estimate(v-if="currentImport.status === 'running' && estimatedCompletion")
                                | {{ $t('Estimated completion: {time}.', { time: estimatedCompletion }) }}
                            .import-controls
                                el-button.pause-import(
                                    v-if="currentImport.status === 'running' && controlUrl('pause')"
                                    size="mini"
                                    icon="el-icon-fa-pause fas"
                                    :loading="controlPending === 'pause'"
                                    @click.stop.prevent="pause"
                                )
                                    | {{ $t('Pause') }}
                                el-button.resume-import(
                                    v-if="currentImport.status === 'paused' && controlUrl('resume')"
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-play fas"
                                    :loading="controlPending === 'resume'"
                                    @click.stop.prevent="resume"
                                )
                                    | {{ $t('Continue') }}
                                el-button.stop-import(
                                    v-if="controlUrl('stop')"
                                    size="mini"
                                    type="danger"
                                    icon="el-icon-fa-ban fas"
                                    :loading="controlPending === 'stop'"
                                    @click.stop.prevent="stop"
                                )
                                    | {{ $t('Cancel import') }}
                template(v-else-if="showLegacyProgress")
                    .fresh(v-if="currentImport.status === 'fresh'")
                        .alert.primary
                            p {{ $t('Your import has been queued and will start as soon as possible.') }}
                            p {{ $t('Please check later') }},&nbsp
                                a(href="#" @click.stop.prevent="initialize") {{ $t('or refresh now') }}
                    .pending(v-if="currentImport.status === 'pending'")
                        .alert.primary
                            p {{ $t('An import for this category is in the queue and will start as soon as possible.') }}
                            p {{ $t('Please check later') }},&nbsp
                                a(href="#" @click.stop.prevent="initialize") {{ $t('or refresh now') }}
                            //- a queued import must be cancellable too, otherwise resuming
                            //- leaves the operator with no way out until the job picks it up
                            .import-controls
                                el-button.stop-import(
                                    v-if="controlUrl('stop')"
                                    size="mini"
                                    type="danger"
                                    icon="el-icon-fa-ban fas"
                                    :loading="controlPending === 'stop'"
                                    @click.stop.prevent="stop"
                                )
                                    | {{ $t('Cancel import') }}
                    .running(v-if="currentImport.status === 'running'")
                        .alert.primary
                            .percentage
                                el-progress(
                                    type="dashboard"
                                    :width="100"
                                    :percentage="currentImport.percentage"
                                    :format="percentage => `${Math.round(percentage * 100) / 100}%`"
                                )
                            .info(v-if="currentImport.percentage < 100")
                                p {{ $t('An import for this category is currently running.') }}
                                p {{ $t('Started: {time}', { time: formatDistanceToNow(currentImport.started_at), percentage: Math.round(currentImport.percentage * 100) / 100 }) }}
                                p(v-if="estimatedCompletion") {{ $t('Estimated completion: {time}.', { time: estimatedCompletion }) }}
                                p {{ $t('Please check later') }},&nbsp
                                    a(href="#" @click.stop.prevent="initialize") {{ $t('or refresh now') }}
                                .import-controls
                                    el-button.pause-import(
                                        v-if="controlUrl('pause')"
                                        size="mini"
                                        icon="el-icon-fa-pause fas"
                                        :loading="controlPending === 'pause'"
                                        @click.stop.prevent="pause"
                                    )
                                        | {{ $t('Pause') }}
                                    el-button.stop-import(
                                        v-if="controlUrl('stop')"
                                        size="mini"
                                        type="danger"
                                        icon="el-icon-fa-ban fas"
                                        :loading="controlPending === 'stop'"
                                        @click.stop.prevent="stop"
                                    )
                                        | {{ $t('Cancel import') }}
                            .info(v-else)
                                p {{ $t('The import has been completed.') }}
                    .paused(v-if="currentImport.status === 'paused'")
                        .alert.primary
                            .percentage
                                //- muted ring: a held import must not read as a live one
                                el-progress(
                                    type="dashboard"
                                    color="#909399"
                                    :width="100"
                                    :percentage="currentImport.percentage"
                                    :format="percentage => `${Math.round(percentage * 100) / 100}%`"
                                )
                            .info
                                p {{ $t('The import is paused and will continue where it stopped.') }}
                                .import-controls
                                    el-button.resume-import(
                                        v-if="controlUrl('resume')"
                                        size="mini"
                                        type="primary"
                                        icon="el-icon-fa-play fas"
                                        :loading="controlPending === 'resume'"
                                        @click.stop.prevent="resume"
                                    )
                                        | {{ $t('Continue') }}
                                    el-button.stop-import(
                                        v-if="controlUrl('stop')"
                                        size="mini"
                                        type="danger"
                                        icon="el-icon-fa-ban fas"
                                        :loading="controlPending === 'stop'"
                                        @click.stop.prevent="stop"
                                    )
                                        | {{ $t('Cancel import') }}
                .canceled(v-if="currentImport.status === 'canceled'")
                    .alert.warning
                        p {{ $t('The last import was cancelled {time} ago. Products imported before that were kept.', { time: formatDistanceToNow(currentImport.finished_at) }) }}
                .failed(v-if="currentImport.status === 'failed'")
                    .alert.danger
                        p {{ $t('The last import could not be completed successfull. It failed {time} ago.', { time: formatDistanceToNow(currentImport.finished_at) }) }}
                .finished(v-if="currentImport.status === 'finished'")
                    .alert.success
                        p {{ $t('The Last import has been finished successfully {time} ago.', { time: formatDistanceToNow(currentImport.finished_at) }) }}

            //- one source of truth with the progress panel above: while an import is active the
            //- form must be gone, or an operator can start a second one on top of a running job
            el-form.dynamic-form(v-if="!unsaved && !importIsActive")
                el-form-item.select.no-margin(
                    :label="$t('Source category')"
                )
                    //- el-select(clearable v-model="form.category")
                        el-option(
                            v-for="(item, index) in categories.items"
                            :key="item.value || item.key"
                            :label="item.label"
                            :value="item.value || item.key"
                        )
                            .option
                                b {{ item.label }}
                                small(v-if="item.count") {{ $t('Articles') }}: {{ item.count }}
                    Treeselect(
                        v-model="form.source"
                        value-format="object"
                        :placeholder="$t('Select source...')"
                        :multiple="false"
                        :options="categories.items"
                        :default-expand-level="Infinity"
                        :close-on-select="true"
                        :open-on-focus="true"
                        :searchable="true"
                        :clearable="true"
                        :always-open="false"
                    )
                        .option(
                            slot="option-label"
                            slot-scope="{ node }"
                        )
                            b(v-if="node && node.raw") {{ node.raw.label }}
                            small(v-if="node && node.raw.count") {{ $t('Articles') }}: {{ node.raw.count }}
                        .value(
                            slot="value-label"
                            slot-scope="{ node }"
                        )
                            label(v-html="getTreeselectValueLabel(node)")
                template(v-for="field in additionalFieldConfigs")
                    InputField(:config="field" :form="form" :key="field.key")
                .inline-form-fields
                    template(v-if="manageStockFieldConfig && manageStockFieldConfig.options && manageStockFieldConfig.options.length")
                        InputField(:config="manageStockFieldConfig" :form="form" :key="manageStockFieldConfig.key" :hide-label="true")
                    .spacer
                    template(v-if="createSubCategoriesFieldConfig && form.source && form.source.children && form.source.children.length")
                        InputField.margin-right(:config="createSubCategoriesFieldConfig" :form="form" :key="createSubCategoriesFieldConfig.key")
                    el-form-item.buttons.no-margin
                        el-button.start-import(
                            type="primary"
                            :disabled="!form.source"
                            @click="start"
                        )
                            | {{ $t('Import') }}
</template>

<i18n>
{
    "de": {
        "Technical issue! Product import could not be initialized.": "Technisches Problem! Produktimport konnte nicht initialisiert werden.",
        "Product import could not be started.": "Produktimport konnte nicht gestartet werden.",
        "This category has not yet been saved. You need to save it in order to import products.": "Diese Kategorie ist noch nicht gespeichert. Sie müssen sie speichern, um Produkte importieren zu können.",
        "An import for this category is in the queue and will start as soon as possible.": "Aktuell befindet sich für diese Kategorie bereits ein Import in der Warteschlange und wird so schnell wie möglich gestartet.",
        "An import for this category is currently running.": "Aktuell befindet sich für diese Kategorie bereits ein Import in der Verarbeitung.",
        "It has been started {time} ago and is currently at {percentage}%.": "Der Import wurde vor {time} gestartet und ist aktuell bei {percentage}%.",
        "Started: {time}": "Gestartet vor: {time}",
        "The Last import has been finished successfully {time} ago.": "Der letzte import wurde erfolgreich abgeschlossen vor {time}.",
        "The last import could not be completed successfull. It failed {time} ago.": "Der letzte Import konnte nicht erfolgreich abgeschlossen werden. Fehlgeschlagen vor {time}.",
        "Estimated completion: {time}.": "Voraussichtliche Dauer bis zur Fertigstellung: {time}",
        "Pause": "Pausieren",
        "Continue": "Fortsetzen",
        "Cancel import": "Import abbrechen",
        "The import is paused and will continue where it stopped.": "Der Import ist pausiert und wird an der unterbrochenen Stelle fortgesetzt.",
        "The last import was cancelled {time} ago. Products imported before that were kept.": "Der letzte Import wurde vor {time} abgebrochen. Bereits importierte Produkte wurden beibehalten.",
        "The import could not be updated ({action}).": "Der Import konnte nicht aktualisiert werden ({action}).",
        "Your import has been queued and will start as soon as possible.": "Dein Import wurde erfolgreich erfasst und wird so schnell wie möglich gestartet.",
        "Please check later": "Schau doch später nochmal vorbei",
        "The import has been completed.": "Der Import wurde erfolgreich abgeschlossen.",
        "Source category": "Quellkategorie",
        "or refresh now": "oder aktualisiere jetzt",
        "With stock": "Mit Lagerbestand",
        "Without stock": "Ohne Lagerbestand",
        "Articles": "Artikel",
        "Select source...": "Quelle wählen...",
        "Create sub-categories": "Unterkategorien erstellen",
        "Import": "Importieren",
        "Try again": "Erneut versuchen"
    },
    "en": {
        "It has been started {time} ago and is currently at {percentage}%.": "It has been started {time} ago and is currently at {percentage}%.",
        "Started: {time}": "Started: {time}",
        "Estimated completion: {time}.": "Estimated completion: {time}.",
        "The last import could not be completed successfull. It failed {time} ago.": "The last import could not be completed successfull. It failed {time} ago.",
        "The Last import has been finished successfully {time} ago.": "The Last import has been finished successfully {time} ago.",
        "The last import was cancelled {time} ago. Products imported before that were kept.": "The last import was cancelled {time} ago. Products imported before that were kept.",
        "The import could not be updated ({action}).": "The import could not be updated ({action})."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { formatDistanceToNow, fromUnixTime, getUnixTime } from 'date-fns';
import { activeDateLocale } from '@/utils/dateLocale';

import { adminClient as apolloClient } from '@/store/apollo';
import pinia from '@/pinia';
import { useSetupStore, useShopCategoriesStore } from '@/stores';

import ProductImportApi from '@/extensions/shop/store/api/productImport';
import StatusLogApi from '@/store/api/system/statusLog';
import StatusLog from '@/views/cms/Dashboard/widgets/logs/Partials/StatusLog';
import InputField from './ProductTransferInputField';
import Echo from '@/echo';

import Treeselect from '@riophae/vue-treeselect';

export default {
    name: 'ProductImport',
    props: {
        config: {
            type: Object,
            required: true,
        },
        category: {
            type: Object,
            required: true,
        }
    },
    data() {
        const target = {
            id: this.category.id,
            key: this.category.key,
            name: this.category.name,
        }
        return {
            info: false,
            error: false,
            actions: {
                initialize: undefined,
                start: undefined,
                stop: undefined,
                pause: undefined,
                resume: undefined,
                refresh: undefined,
            },
            categories: {
                items: [],
                loading: false,
            },
            currentImport: undefined,
            // which control is mid-request, so only that button spins
            controlPending: false,
            // exactly one of these is ever active - see applyProgressMode()
            statusLogChannel: undefined,
            pollTimer: undefined,
            // the StatusLog row the shared component renders, seeded once then kept
            // current by the status-log.{id} broadcasts
            statusLog: null,
            // true while the seed request is in flight, and whether it failed - together
            // these decide whether the legacy block is allowed to render at all
            statusLogFetching: false,
            statusLogFailed: false,
            form: {
                target,
                source: undefined,
            }
        }
    },
    components: {
        InputField,
        Treeselect,
        StatusLog,
    },
    computed: {
        unsaved() {
            return !this.category.id || _.startsWith(this.category.id, 'new:')
        },
        // The websocket bar REPLACES the polled one, and only when the microservice actually
        // delivers a status log. Several services keep none at all, so polling is a
        // permanently supported mode, not a temporary fallback. Decided from the data, never
        // from a service list: a service that has the column can still return null when the
        // log failed to create, and that import must poll.
        liveProgress() {
            return Boolean(_.get(this.currentImport, 'status_log_id')) && Boolean(Echo.client);
        },
        importIsActive() {
            return ['fresh', 'pending', 'running', 'paused'].includes(_.get(this.currentImport, 'status'));
        },
        // a queued row has not produced a status log event yet, so there is nothing to listen
        // to until the job actually starts - see applyProgressMode()
        importIsQueued() {
            return ['fresh', 'pending'].includes(_.get(this.currentImport, 'status'));
        },
        // the shared StatusLog component takes over only once we actually hold a log row
        useLiveStatusLog() {
            return this.liveProgress && this.importIsActive && Boolean(this.statusLog);
        },
        // The legacy blocks exist for microservices that keep no status log at all.
        // Once one exists we always show the shared component - including while the row
        // is queued after a resume, where the last known log still describes the run.
        // Only a failed seed falls back, so the panel can never end up blank.
        showLegacyProgress() {
            return !this.liveProgress || this.statusLogFailed;
        },
        estimatedCompletion() {
            if (!this.currentImport || this.currentImport.percentage <= 0) return false;

            const secondsSinceStarted = getUnixTime(new Date) - this.currentImport.started_at;
            const estimatedDuration = parseInt(secondsSinceStarted / this.currentImport.percentage * 100);

            return this.formatDistanceToNow(estimatedDuration + parseInt(this.currentImport.started_at));
        },
        manageStockFieldConfig() {
            const fields = _.get(this.config, 'fields') || []
            const config = fields.find(el => el.key === 'manage-stock')
            return config
        },
        createSubCategoriesFieldConfig() {
            const fields = _.get(this.config, 'fields') || []
            const config = fields.find(el => el.key === 'create-sub-categories')
            return config
        },
        additionalFieldConfigs() {
            const fields = _.get(this.config, 'fields') || []
            return fields.filter(el => ['manage-stock', 'create-sub-categories'].includes(el.key) === false)
        },
        setupStore() {
            return useSetupStore();
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        settingValues() {
            return this.setupStore.settingValues;
        },
        categoriesStore() {
            return useShopCategoriesStore(pinia);
        },
    },
    methods: {
        formatDistanceToNow(timestamp) {
            return formatDistanceToNow(fromUnixTime(parseInt(timestamp)), { locale: activeDateLocale() })
        },
        initialize() {
            if (!this.actions.initialize) {
                this.error = this.$t('Technical issue! Product import could not be initialized.')
                return;
            }

            const settings = this.settingValues;

            let api = this.actions.initialize.api;
            let url = this.actions.initialize.url;
            let token = this.actions.initialize.token;

            const prefix = 'settings:'
            if (url && url.startsWith('settings:')) {
                url = _.get(settings, url.substr(prefix.length, url.length - prefix.length))
            }
            if (token && token.startsWith('settings:')) {
                token = _.get(settings, token.substr(prefix.length, token.length - prefix.length))
            }

            // console.log(key, api, url, token, data);

            let method, config;
            switch (api.toLowerCase()) {
                case 'rest':
                    if (token) {
                        config = {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        };
                    }
                    method = ProductImportApi.REST;
                    break;

                // TODO
                // case 'graphql':
                //     method = MicroserviceApi.REST
                //     break;
            }

            // fetch categories
            if (!method) {
                this.error = this.$t('Technical issue! Product import could not be initialized.')
                return;
            }

            this.categories.loading = true;

            const data = {
                category: { id: this.category.id },
                cms: { id: this.activeCms.id }
            }
            method(url, data, config)
                .then(({ data }) => {
                    this.error = false
                    if (data.import) {
                        this.currentImport = data.import;
                        // this is also the seed for the live bar: the socket only delivers
                        // the NEXT event, so without it a reload mid-import shows 0%
                        this.applyProgressMode();
                    }

                    const request = _.get(data, 'import.request');
                    const sourceID = _.get(request, 'source.id');

                    if (data.tree && data.tree.length) {
                        this.categories.items = data.tree;
                        const findActiveCategory = (categories) => {
                            categories.forEach(category => {
                                if (!this.form.source && sourceID && (category.active || category.id === sourceID)) {
                                    this.$set(this.form, 'source', category)
                                }
                                if (!this.form.source && category.children && category.children.length) findActiveCategory(category.children)
                            })
                        }
                        findActiveCategory(data.tree)
                    } else {
                        this.categories.items = [];
                    }

                    if (request && typeof request === 'object') {
                        Object.entries(request).forEach(([key, value]) => {
                            if (['source', 'target'].includes(key) === false) this.$set(this.form, key, value);
                        })
                    }
                })
                .catch(error => {
                    // eslint-disable-next-line
                    console.error(error);
                    this.error = this.$t('Technical issue! Product import could not be initialized.')
                })
                .finally(() => {
                    this.categories.loading = false;
                })
        },
        getTreeselectValueLabel(node) {
            if (! node) return

            let label = `${node.raw.label}`;
            const addPrefix = node => {
                if (node.parentNode) {
                    label = `${node.parentNode.raw.label} <i class="el-icon-fa-angle-right fas"></i> ${label}`
                    addPrefix(node.parentNode);
                }
            }
            addPrefix(node);
            return label;
        },
        start() {
            if (!this.actions.start) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Product import could not be started.'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return;
            }

            const settings = this.settingValues;

            let api = this.actions.start.api;
            let url = this.actions.start.url;
            let token = this.actions.start.token;

            const prefix = 'settings:'
            if (url && url.startsWith('settings:')) {
                url = _.get(settings, url.substr(prefix.length, url.length - prefix.length))
            }
            if (token && token.startsWith('settings:')) {
                token = _.get(settings, token.substr(prefix.length, token.length - prefix.length))
            }

            // console.log(key, api, url, token, data);

            let method, config;
            switch (api.toLowerCase()) {
                case 'rest':
                    if (token) {
                        config = {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        };
                    }
                    method = ProductImportApi.REST;
                    break;

                // TODO
                // case 'graphql':
                //     method = MicroserviceApi.REST
                //     break;
            }

            // fetch categories
            if (!method) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Product import could not be started.'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return;
            }

            this.categories.loading = true;

            apolloClient.clearStore().then(() => {
                method(url, this.form, config)
                    .then(({ data }) => {
                        // reload categories form

                        this.currentImport = data
                        this.applyProgressMode();
                        this.fetchCategories(this.activeCms.shop.id)
                    })
                    .catch(error => {
                        // eslint-disable-next-line
                        console.error(error);
                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            message: this.$t('Product import could not be started.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        })
                    })
                    .finally(() => {
                        this.categories.loading = false;
                    })
            })
        },
        fetchCategories(shopId) {
            return this.categoriesStore.fetchCategories(shopId);
        },

        // ---- progress: websocket when the service keeps a status log, polling otherwise ----

        // Re-evaluated whenever currentImport is replaced. Tears the other mode down first so
        // the two can never run at once - a leaked interval behind a live bar looks exactly
        // like the feature working.
        applyProgressMode() {
            this.teardownProgress();

            if (!this.importIsActive) {
                this.statusLog = null;
                return;
            }

            // Feed the shared component in EVERY active state, queued included. The log row
            // still describes this import across pause -> pending -> running, so dropping it
            // between modes is what made the legacy block flash on screen after Continue.
            if (this.liveProgress) this.seedStatusLog();

            // A queued row emits no status log events at all - the job has not run yet - so
            // the socket has nothing to say until it starts. Polling is the only way to learn
            // that start/resume actually got picked up; without it the panel sits on the
            // queued branch forever. Once running, the socket takes over and polling stops.
            if (this.importIsQueued || !this.liveProgress) this.startPolling();
            else this.subscribeToStatusLog();
        },

        subscribeToStatusLog() {
            const logId = _.get(this.currentImport, 'status_log_id');
            if (!logId) return;

            if (!Echo.client) Echo.initialize();
            if (!Echo.client) return;

            this.statusLogChannel = `status-log.${logId}`;
            Echo.client.private(this.statusLogChannel)
                .listen('.status-log:updated', data => {
                    const log = _.get(data, 'log');
                    if (!log || !this.currentImport) return;

                    // feed the shared component the same way StatusLogsTable does
                    if (this.statusLog) {
                        ['status', 'progress', 'updated_at', 'heading', 'body', 'data'].forEach(key => {
                            this.$set(this.statusLog, key, log[key]);
                        });
                    } else {
                        this.seedStatusLog();
                    }

                    // progress is a float 0-100 on the log; the import row calls it percentage
                    if (log.progress !== null && log.progress !== undefined) {
                        this.$set(this.currentImport, 'percentage', log.progress);
                    }

                    // the log reaching a terminal state means the row has moved on too, and
                    // the row carries things the log does not (finished_at, status)
                    if (['success', 'error', 'canceled'].includes(log.status)) this.initialize();
                });
        },

        seedStatusLog() {
            const logId = _.get(this.currentImport, 'status_log_id');

            if (!logId) {
                this.statusLog = null;
                return;
            }

            // already holding this exact log - keep it rather than blanking the panel
            if (this.statusLog && String(this.statusLog.id) === String(logId)) return;

            this.fetchStatusLog(logId);
        },

        fetchStatusLog(logId) {
            this.statusLogFetching = true;
            this.statusLogFailed = false;

            return StatusLogApi.fetchList({ id: parseInt(logId) })
                .then(logs => {
                    this.statusLog = _.head(logs) || null;
                    this.statusLogFailed = !this.statusLog;
                })
                .catch(() => {
                    this.statusLog = null;
                    this.statusLogFailed = true;
                })
                .finally(() => { this.statusLogFetching = false; });
        },

        startPolling() {
            // waiting for a queued job to be picked up is a short transition, so ask more
            // often than in the legacy steady state - initialize() re-runs applyProgressMode(),
            // which hands over to the socket the moment the row turns 'running'
            const interval = this.importIsQueued ? 3000 : 10000;

            this.pollTimer = setInterval(() => {
                if (!this.importIsActive) {
                    this.teardownProgress();
                    return;
                }
                this.initialize();
            }, interval);
        },

        teardownProgress() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = undefined;
            }
            if (this.statusLogChannel) {
                // the channel is per-log and exclusively ours, so leave rather than
                // stopListening (which would keep the subscription open)
                if (Echo.client) Echo.client.leave(this.statusLogChannel);
                this.statusLogChannel = undefined;
            }
        },

        // ---- controls ----

        stop() {
            return this.sendControl('stop');
        },
        pause() {
            return this.sendControl('pause');
        },
        resume() {
            return this.sendControl('resume');
        },

        // Resolve a `settings:` reference to its actual value. Returns undefined when the
        // shop has not configured that URL, which is how the buttons stay hidden rather than
        // rendering and then failing on click.
        resolveActionValue(value) {
            const prefix = 'settings:';
            if (!value) return undefined;
            if (!value.startsWith(prefix)) return value;

            return _.get(this.settingValues, value.substr(prefix.length, value.length - prefix.length)) || undefined;
        },

        controlUrl(action) {
            return this.resolveActionValue(_.get(this.actions, `${action}.url`));
        },

        sendControl(action) {
            const id = _.get(this.currentImport, 'id');
            if (!id || this.controlPending) return Promise.resolve();

            const url = this.controlUrl(action);
            const token = this.resolveActionValue(_.get(this.actions, `${action}.token`));

            if (!url) {
                this.notifyControlError(action);
                return Promise.resolve();
            }

            const requestConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : null;

            this.controlPending = action;
            return ProductImportApi.REST(url, { id }, requestConfig)
                .then(({ data }) => {
                    this.currentImport = data;
                    this.applyProgressMode();
                })
                .catch(() => this.notifyControlError(action))
                .finally(() => { this.controlPending = false; });
        },

        notifyControlError(action) {
            this.$notify.error({
                title: this.$t('Error'),
                message: this.$t('The import could not be updated ({action}).', { action }),
                iconClass: 'fas el-icon-fa-triangle-exclamation',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
    },
    beforeDestroy() {
        this.teardownProgress();
    },
    mounted() {
        // const key = _.get(this.config, 'key');
        this.actions.initialize = _.get(this.config, 'initialize');
        this.actions.start = _.get(this.config, 'start');
        this.actions.stop = _.get(this.config, 'stop');
        this.actions.pause = _.get(this.config, 'pause');
        this.actions.resume = _.get(this.config, 'resume');
        this.actions.refresh = _.get(this.config, 'refresh');

        // set default field values
        const fields = _.get(this.config, 'fields') || []
        fields.forEach(field => {
            switch (field.type) {
                case 'radio':
                case 'boolean-button-group':
                    if (field.options && field.options.length) {
                        const defaultOption = field.options.find(el => el.default === true)
                        if (defaultOption) this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(defaultOption.key))
                    } else {
                        this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(field.key))
                    }
                    break;

                case 'switch':
                case 'checkbox':
                case 'boolean-button':
                    this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(field.value))
                    break;

                case 'multi-select':
                    // options hidden by their conditions are pruned again
                    // by ProductTransferInputField once it knows about the shop
                    this.$set(this.form, field.key, (field.options || [])
                        .filter(option => option.default === true)
                        .map(option => option.key))
                    break;

                default:
                    this.$set(this.form, field.key, field.value)

            }
        })

        this.form.cms = this.activeCms.id

        if (this.unsaved) {
            this.info = this.$t('This category has not yet been saved. You need to save it in order to import products.')
        } else {
            this.initialize()
        }
    }
}
</script>

<style lang="sass" scoped>
.product-import
    min-height: 40px
    .import-controls
        display: flex
        gap: .5rem
        margin-top: .75rem

    //- inside the shared StatusLog the actions slot sits in a centred flex row,
    //- so the block-level spacing the legacy alerts need would misalign it
    .live-status
        .estimate
            white-space: nowrap
            color: $--color-text-secondary
        .import-controls
            margin-top: 0

    .alert
        margin: 0
        & + .alert
            margin-top: 1rem

    .option
        padding: 0.5rem 0
        small, b
            display: block
            line-height: initial

    .inline-form-fields
        display: flex
        .el-form-item + .el-form-item
            margin-left: 0.5rem

    .import-info-panel + .dynamic-form
        margin-top: 1rem

    .dynamic-form > .el-form-item
        margin: 1rem 0

    .spacer
        flex-grow: 1

    .margin-right
        margin-right: 1rem

    .el-button-group,
    .el-form-item.buttons
        white-space: nowrap

    .el-button-group
        .el-button.is-plain
            position: relative
            // border: 1px solid
            &:active,
            &:focus
                color: $--shop-color-primary
                background: lighten($--shop-color-primary, 35%)
                border-color: $--shop-color-primary
        .el-button.is-plain.el-button--primary
            z-index: 1
            // pointer-events: none
            &:hover
                color: $--shop-color-primary
                background: lighten($--shop-color-primary, 35%)
                border-color: $--shop-color-primary
    // .percentage
        margin-bottom: 0.5rem
        .el-progress.el-progress--line
            display: flex
            align-items: center
            justify-content: space-between
            ::v-deep .el-progress__text
                text-align: right
    //- every state that shows a progress ring shares one layout, so a new
    //- state cannot end up stacking the ring on top of its text again
    .running .alert,
    .paused .alert
        display: flex
        align-items: center
        .percentage
            flex: 0 0 auto
            margin-right: 1rem
            margin-bottom: -1rem
            ::v-deep
                .el-progress__text
                    padding-left: 0.5rem
                path.el-progress-circle__track
                    stroke: #dad4e5 !important
        .info
            flex: 1 1 auto
            min-width: 0
</style>
